import { supabase } from '@/lib/supabase';
import {
  MonthlyPnL,
  StockSummary,
  StockAgeing,
  LowStockAccessory,
} from '@/types/models';

export async function fetchMonthlyPnL(): Promise<MonthlyPnL[]> {
  const { data, error } = await supabase
    .from('monthly_pnl')
    .select('*')
    .order('month', { ascending: false });

  if (error) throw error;
  return (data ?? []) as MonthlyPnL[];
}

export async function fetchStockSummary(): Promise<StockSummary[]> {
  const { data, error } = await supabase
    .from('stock_summary')
    .select('*');

  if (error) throw error;
  return (data ?? []) as StockSummary[];
}

export async function fetchStockAgeing(): Promise<StockAgeing[]> {
  const { data, error } = await supabase
    .from('stock_ageing')
    .select('*')
    .order('age_days', { ascending: false });

  if (error) throw error;
  return (data ?? []) as StockAgeing[];
}

export async function fetchLowStockAccessoriesReport(): Promise<LowStockAccessory[]> {
  const { data, error } = await supabase
    .from('low_stock_accessories')
    .select('*');

  if (error) throw error;
  return (data ?? []) as LowStockAccessory[];
}

export interface SalesByDayItem {
  date: string;
  salesCount: number;
  totalRevenue: number;
}

export async function fetchSalesByDay(days = 30): Promise<SalesByDayItem[]> {
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - days);

  const { data, error } = await supabase
    .from('sales')
    .select('sale_date, total, status')
    .gte('sale_date', startDate.toISOString())
    .in('status', ['completed', 'partially_refunded']);

  if (error) return [];

  const map: Record<string, { count: number; revenue: number }> = {};
  (data ?? []).forEach((s) => {
    const d = s.sale_date.slice(0, 10);
    if (!map[d]) map[d] = { count: 0, revenue: 0 };
    map[d].count += 1;
    map[d].revenue += Number(s.total) || 0;
  });

  return Object.keys(map)
    .sort()
    .map((date) => ({
      date,
      salesCount: map[date].count,
      totalRevenue: map[date].revenue,
    }));
}

export interface TopSellerItem {
  itemName: string;
  itemType: string;
  totalQuantity: number;
  totalRevenue: number;
}

export async function fetchTopSellers(): Promise<TopSellerItem[]> {
  const { data, error } = await supabase
    .from('sale_items')
    .select('item_name, item_type, quantity, line_total')
    .limit(200);

  if (error) return [];

  const map: Record<string, TopSellerItem> = {};
  (data ?? []).forEach((si) => {
    const key = `${si.item_type}:${si.item_name}`;
    if (!map[key]) {
      map[key] = {
        itemName: si.item_name,
        itemType: si.item_type,
        totalQuantity: 0,
        totalRevenue: 0,
      };
    }
    map[key].totalQuantity += Number(si.quantity) || 0;
    map[key].totalRevenue += Number(si.line_total) || 0;
  });

  return Object.values(map)
    .sort((a, b) => b.totalRevenue - a.totalRevenue)
    .slice(0, 10);
}

export async function fetchCustomerBalancesSummary() {
  const { data, error } = await supabase
    .from('customer_balances_summary')
    .select('*')
    .order('outstanding_balance', { ascending: false });

  if (error) throw error;
  return data ?? [];
}

export async function fetchSupplierBalancesSummary() {
  const { data, error } = await supabase
    .from('supplier_balances_summary')
    .select('*')
    .order('outstanding_payable', { ascending: false });

  if (error) throw error;
  return data ?? [];
}

export async function fetchRepairsPnLSummary() {
  const { data, error } = await supabase
    .from('repairs_pnl_summary')
    .select('*')
    .order('month', { ascending: false });

  if (error) throw error;
  return data ?? [];
}

